import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, ChevronRight } from 'lucide-react'
import type { CategoryScore, CategoryKey } from '../../types'
import { gradeLabel } from '../../lib/utils'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../../config'

interface CategorySidebarProps {
  overallScore: number
  categoryScores: Partial<Record<CategoryKey, CategoryScore>>
  businessName: string
}

const CATEGORY_ORDER: CategoryKey[] = ['search_results', 'guest_experience', 'local_listings']

function sidebarGaugeStroke(pct: number): string {
  if (pct >= 70) return '#58a036'
  if (pct >= 50) return '#f19324'
  return '#dc2626'
}

function sidebarGradeColor(pct: number): string {
  if (pct >= 80) return '#58a036'
  if (pct >= 60) return '#f19324'
  return '#dc2626'
}

/** 270° arc open at the bottom (Figma gauge shape). */
function SemiArcGauge({ score, max = 100 }: { score: number; max?: number }) {
  const [displayed, setDisplayed] = useState(0)
  const animRef = useRef<number | null>(null)

  const width = 252
  const height = 152
  const strokeWidth = 14
  const r = 84
  const cx = width / 2
  const cy = 90
  const pct = displayed / max
  const stroke = sidebarGaugeStroke((score / max) * 100)

  // Clockwise from 12 o'clock: bottom-left (225°) → over top → bottom-right (135°)
  const polar = (clockDeg: number) => {
    const rad = (clockDeg * Math.PI) / 180
    return {
      x: cx + r * Math.sin(rad),
      y: cy - r * Math.cos(rad),
    }
  }
  const start = polar(225)
  const end = polar(135)
  const arcPath = `M ${start.x} ${start.y} A ${r} ${r} 0 1 1 ${end.x} ${end.y}`
  const arcLength = 1.5 * Math.PI * r

  useEffect(() => {
    const startTime = Date.now()
    const duration = 1200
    const tick = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(Math.round(score * eased))
      if (progress < 1) animRef.current = requestAnimationFrame(tick)
    }
    animRef.current = requestAnimationFrame(tick)
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
  }, [score])

  return (
    <div className="relative mx-auto w-full max-w-[252px] h-[152px]">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-[152px] block"
        aria-hidden
      >
        <path
          d={arcPath}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d={arcPath}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={arcLength}
          strokeDashoffset={arcLength * (1 - pct)}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)' }}
        />
      </svg>
      <div className="absolute inset-x-0 top-[38px] flex flex-col items-center pointer-events-none">
        <span className="text-[50px] font-bold leading-[1] text-[#101828] tabular-nums">{displayed}</span>
        <span className="text-[17px] text-[#99a1af] leading-none mt-1">of {max}</span>
      </div>
    </div>
  )
}

function MiniScoreRing({ score, max, color }: { score: number; max: number; color: string }) {
  const size = 43
  const strokeWidth = 4
  const r = (size - strokeWidth) / 2 - 1
  const c = size / 2
  const circ = 2 * Math.PI * r
  const offset = circ * (1 - score / max)

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={c} cy={c} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${c} ${c})`}
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[11px] leading-none whitespace-nowrap">
        <span className="font-bold text-[#364153]">{score}</span>
        <span className="font-normal text-[#99a1af]">/{max}</span>
      </div>
    </div>
  )
}

/**
 * CategorySidebar — left-panel score summary (Figma Aside).
 * Labels are translated via the 'scoring' namespace.
 */
export function CategorySidebar({ overallScore, categoryScores, businessName }: CategorySidebarProps) {
  const { t } = useTranslation('scoring')

  return (
    <aside className="w-full shrink-0 lg:w-72">
      {/* lg:top-[104px]: the report's own header is `sticky top-0` and 80px
          tall (min-h-[80px]) with a higher z-index than this card. A `top`
          offset smaller than the header's height (the old `lg:top-6` = 24px)
          makes this card stick *underneath* the header's sticky zone, so the
          header visually clips the card's top edge right at the moment it
          catches — the reported jump. 104px = 80px header + the original
          24px breathing room, so the card sticks directly below the header
          instead of behind it. Kept as an arbitrary value rather than the
          "canonical" `top-26` suggestion — 26 isn't in Tailwind v3's fixed
          spacing scale (only v4's dynamic scale supports it), and this
          component is also reused as-is by the admin dashboard's Tailwind
          v3 build. */}
      <div className="rounded-2xl bg-white px-5 py-6 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] lg:sticky lg:top-[104px]">
        {/* Semi-arc overall score */}
        <SemiArcGauge score={overallScore} />
        <div className="text-center mt-1 mb-3">
          <p className="text-xs text-[#6a7282] leading-4">{t('sidebar.onlineHealth')}</p>
          <p className="text-sm font-bold text-[#364153] mt-0.5">
            {t(gradeLabel(overallScore))}
          </p>
        </div>

        {/* Business name */}
        <p
          className="text-center text-xs text-[#6a7282] mb-5 truncate px-1 leading-4"
          title={businessName}
          dir="auto"
        >
          {businessName}
        </p>

        {/* Category rows — clickable with chevron */}
        <div className="space-y-1">
          {CATEGORY_ORDER.map((key) => {
            const cat = categoryScores[key]
            if (!cat) return null
            const pct = (cat.score / cat.max_score) * 100
            const stroke = sidebarGaugeStroke(pct)
            const gradeColor = sidebarGradeColor(pct)
            const label = t(gradeLabel(pct))

            return (
              <a
                key={key}
                href={`#${key}`}
                className="flex items-center gap-2 rounded-xl px-2 py-2.5 transition-colors hover:bg-[#f9fafb] group"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#1e2939] leading-[17.5px]">
                    {t(`category.${key}`)}
                  </p>
                  <p className="text-xs font-bold mt-0.5 leading-4" style={{ color: gradeColor }}>
                    {label}
                  </p>
                </div>
                <MiniScoreRing score={cat.score} max={cat.max_score} color={stroke} />
                <ChevronRight className="w-4 h-4 text-[#cbd5e1] shrink-0 group-hover:text-[#99a1af] transition-colors" />
              </a>
            )
          })}
        </div>

        {/* CTA */}
        <div className="mt-5 rounded-[14px] bg-[#f2f2f6] px-4 py-4">
          <p className="text-xs font-bold text-[#01114f] mb-1.5 leading-snug">
            {t('sidebar.fixEverything')}
          </p>
          <p className="text-[11px] text-[#7f7f7f] leading-relaxed mb-3">
            {t('sidebar.fixDesc', { appName: APP_NAME })}
          </p>
          <a
            href={CONSULTATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-1.5 text-white text-xs font-semibold py-2.5 px-3 rounded-lg transition-colors max-lg:min-h-11"
            style={{ backgroundColor: BRAND.red }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.red }}
          >
            <Calendar className="w-3.5 h-3.5" />
            {t('sidebar.bookCta')}
          </a>
        </div>
      </div>
    </aside>
  )
}
